import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ReceiptIndianRupee, FileDown, Sheet, Plus, Trash2, Pencil, Search } from "lucide-react";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const n = (v) => Number(v || 0);
const MODES = ["Bank Transfer", "NEFT", "RTGS", "UPI", "Cheque", "Cash", "Input Credit Adjustment"];
const TABS = [
  { id: "dashboard", label: "GST Dashboard" },
  { id: "purchase", label: "Purchase GST Register" },
  { id: "sales", label: "Sales GST Register" },
  { id: "monthly", label: "Monthly Summary" },
  { id: "payments", label: "GST Payments" },
];

const iso = (d) => d.toISOString().slice(0, 10);
const fyStart = (d) => new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);
const presetRange = (key) => {
  const t = new Date();
  if (key === "this_month") return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(new Date(t.getFullYear(), t.getMonth() + 1, 0))];
  if (key === "prev_month") return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))];
  if (key === "this_quarter") { const q = Math.floor(t.getMonth() / 3) * 3; return [iso(new Date(t.getFullYear(), q, 1)), iso(new Date(t.getFullYear(), q + 3, 0))]; }
  if (key === "fy") { const s = fyStart(t); return [iso(s), iso(new Date(s.getFullYear() + 1, 2, 31))]; }
  return ["", ""];
};
const fyLabel = () => { const s = fyStart(new Date()); return `${s.getFullYear()}-${String(s.getFullYear() + 1).slice(2)}`; };

const Card = ({ label, value, sub, tone = "text-slate-900", testid, dark }) => (
  <div data-testid={testid} className={`${dark ? "bg-[#0B132B] border-[#0B132B]" : "bg-white border-slate-200"} border rounded-lg p-4 shadow-sm`}>
    <div className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-500"}`}>{label}</div>
    <div className={`text-lg font-mono font-semibold tabular-nums mt-1.5 break-all ${dark ? "text-white" : tone}`}>{value}</div>
    {sub && <div className={`text-xs mt-1 ${dark ? "text-slate-400" : "text-slate-400"}`}>{sub}</div>}
  </div>
);

const Rows = ({ rows }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-1.5 text-sm">
    {rows.map(([k, v, bold]) => (
      <div key={k} className={`flex justify-between ${bold ? "font-semibold border-t border-slate-200 pt-1.5 mt-1.5 text-slate-900" : "text-slate-600"}`}>
        <span>{k}</span><span className="font-mono tabular-nums">{v}</span>
      </div>
    ))}
  </div>
);

export default function GSTPage() {
  const { user } = useAuth();
  const can = (a) => hasPerm(user, "gst", a);
  const [tab, setTab] = useState("dashboard");
  const [preset, setPreset] = useState("fy");
  const [range, setRange] = useState(presetRange("fy"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [gstin, setGstin] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [start, end] = range;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/gst/summary", { params: { start, end } });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (key) => { setPreset(key); if (key !== "custom") setRange(presetRange(key)); };

  const token = localStorage.getItem("token");
  const qs = `token=${token}&start=${start}&end=${end}`;

  const filterRows = (rows, noKey) => rows.filter((r) =>
    (!q || [r[noKey], r.party, r.gstin].some((x) => String(x || "").toLowerCase().includes(q.toLowerCase()))) &&
    (!gstin || String(r.gstin || "").toLowerCase().includes(gstin.toLowerCase())));

  const purchaseRows = useMemo(() => filterRows(data?.purchase_register || [], "bill_no"), [data, q, gstin]);
  const salesRows = useMemo(() => filterRows(data?.sales_register || [], "doc_no"), [data, q, gstin]);
  const sumOf = (rows, k) => rows.reduce((s, r) => s + n(r[k]), 0);

  const openPay = (p) => {
    setPayForm(p ? { ...p } : {
      payment_date: iso(new Date()), financial_year: fyLabel(), tax_period: "",
      amount_paid: "", cgst_paid: "", sgst_paid: "", igst_paid: "", challan_no: "", payment_mode: "Bank Transfer", remarks: "",
    });
    setPayOpen(true);
  };
  const savePay = async () => {
    if (!payForm.payment_date) return toast.error("Payment date is required");
    setSaving(true);
    try {
      const body = {
        payment_date: payForm.payment_date, financial_year: payForm.financial_year || "", tax_period: payForm.tax_period || "",
        amount_paid: n(payForm.amount_paid), cgst_paid: n(payForm.cgst_paid), sgst_paid: n(payForm.sgst_paid),
        igst_paid: n(payForm.igst_paid), challan_no: payForm.challan_no || "", payment_mode: payForm.payment_mode || "Bank Transfer",
        remarks: payForm.remarks || "", attachments: payForm.attachments || [],
      };
      if (payForm.id) await api.put(`/gst/payments/${payForm.id}`, body);
      else await api.post("/gst/payments", body);
      toast.success(payForm.id ? "GST payment updated" : "GST payment recorded");
      setPayOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const delPay = async (id) => {
    if (!window.confirm("Delete this GST payment record?")) return;
    try { await api.delete(`/gst/payments/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const positionTone = data?.net?.net_gst > 0 ? "text-rose-700" : data?.net?.net_gst < 0 ? "text-emerald-700" : "text-slate-700";

  return (
    <div data-testid="gst-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <ReceiptIndianRupee className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">GST / Tax</h1>
            <p className="text-sm text-slate-500 mt-0.5" data-testid="gst-company-gstin">
              {data?.company?.name || ""}{data?.company?.gstin ? ` · GSTIN ${data.company.gstin}` : " · GSTIN not set in Company Settings"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {can("export_pdf") && <Button data-testid="gst-pdf" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/pdf/gst_summary?${qs}`, "_blank")}><FileDown className="w-4 h-4" /> GST Summary PDF</Button>}
          {can("export_excel") && <Button data-testid="gst-xlsx" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/gst/xlsx?${qs}`, "_blank")}><Sheet className="w-4 h-4" /> Excel</Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <Select value={preset} onValueChange={applyPreset}>
          <SelectTrigger data-testid="gst-period" className="w-44 bg-white h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="prev_month">Previous Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="fy">Financial Year</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="gst-start" type="date" value={start} onChange={(e) => { setPreset("custom"); setRange([e.target.value, end]); }} className="bg-white h-9 w-40" />
        <Input data-testid="gst-end" type="date" value={end} onChange={(e) => { setPreset("custom"); setRange([start, e.target.value]); }} className="bg-white h-9 w-40" />
        <Button data-testid="gst-apply" variant="outline" className="rounded-sm h-9" onClick={load}>Apply</Button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`gst-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm rounded-t-sm ${tab === t.id ? "bg-white border border-b-white border-slate-200 -mb-px font-semibold text-[#0B132B]" : "text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>))}
      </div>

      {loading || !data ? <div className="py-16 text-center text-slate-400" data-testid="gst-loading">Loading GST data...</div> : (
        <>
          {tab === "dashboard" && (
            <div className="space-y-5" data-testid="gst-dashboard">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card testid="gst-input-total" label="Total Input GST (Purchases)" value={fmtINR(data.input_gst.total_gst)} sub={`on ${fmtINR(data.input_gst.taxable)} taxable · ${data.counts.purchase_bills} bills`} tone="text-blue-800" />
                <Card testid="gst-output-total" label="Total Output GST (Sales)" value={fmtINR(data.output_gst.total_gst)} sub={`on ${fmtINR(data.output_gst.taxable)} taxable · ${data.counts.sales_docs} documents`} tone="text-indigo-800" />
                <Card testid="gst-net-position" label={data.net.position} value={fmtINR(Math.abs(data.net.net_gst))} sub="Output GST − Input GST" dark />
                <Card testid="gst-remaining" label={data.remaining.label} value={fmtINR(data.remaining.amount)} sub={`${fmtINR(data.payments_made.total)} already paid to government`} tone={positionTone} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div data-testid="gst-input-breakup">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Input GST (paid on purchases)</div>
                  <Rows rows={[["Taxable purchase value", fmtINR(data.input_gst.taxable)], ["Input CGST", fmtINR(data.input_gst.cgst)],
                    ["Input SGST", fmtINR(data.input_gst.sgst)], ["Input IGST", fmtINR(data.input_gst.igst)],
                    ["Total Input GST", fmtINR(data.input_gst.total_gst), true]]} />
                </div>
                <div data-testid="gst-output-breakup">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Output GST (collected on sales)</div>
                  <Rows rows={[["Taxable sales value", fmtINR(data.output_gst.taxable)], ["Output CGST", fmtINR(data.output_gst.cgst)],
                    ["Output SGST", fmtINR(data.output_gst.sgst)], ["Output IGST", fmtINR(data.output_gst.igst)],
                    ["Total Output GST", fmtINR(data.output_gst.total_gst), true]]} />
                  {data.credit_notes.total_gst > 0 && <p className="text-[11px] text-slate-500 mt-1">Credit notes / sales returns of {fmtINR(data.credit_notes.total_gst)} GST are already deducted above.</p>}
                </div>
                <div data-testid="gst-net-breakup">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Net position</div>
                  <Rows rows={[["Net CGST", fmtINR(data.net.cgst)], ["Net SGST", fmtINR(data.net.sgst)], ["Net IGST", fmtINR(data.net.igst)],
                    ["Net GST Before Government Payments", fmtINR(data.net.net_gst), true],
                    ["GST Already Paid", fmtINR(data.payments_made.total)],
                    [data.remaining.label, fmtINR(data.remaining.amount), true]]} />
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Basis of preparation</div>
                <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">{(data.basis || []).map((b, i) => <li key={i}>{b}</li>)}</ul>
              </div>
            </div>
          )}

          {(tab === "purchase" || tab === "sales") && (
            <div data-testid={`gst-register-${tab}`}>
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input data-testid="gst-search" placeholder="Search bill / party / GSTIN..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 bg-white h-9 w-72" /></div>
                <Input data-testid="gst-gstin-filter" placeholder="GSTIN filter" value={gstin} onChange={(e) => setGstin(e.target.value)} className="bg-white h-9 w-52" />
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    {(tab === "purchase"
                      ? ["Date", "Purchase Bill No", "Supplier", "Supplier GSTIN", "Taxable Value", "CGST", "SGST", "IGST", "Total GST", "Total Bill Value", "Status"]
                      : ["Date", "Invoice No", "Document", "Customer", "Customer GSTIN", "Taxable Value", "CGST", "SGST", "IGST", "Total GST", "Invoice Value", "Status"]
                    ).map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {(tab === "purchase" ? purchaseRows : salesRows).length === 0 ? (
                      <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-400" data-testid="gst-register-empty">No documents in this period</td></tr>
                    ) : (tab === "purchase" ? purchaseRows : salesRows).map((r) => (
                      <tr key={`${r.id}-${r.doc_no || r.bill_no}`} data-testid={`gst-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(r.date)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{r.bill_no || r.doc_no}</td>
                        {tab === "sales" && <td className="px-3 py-2 whitespace-nowrap text-slate-500">{r.doc_type}</td>}
                        <td className="px-3 py-2 text-slate-700">{r.party}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-600">{r.gstin}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(r.taxable)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(r.cgst)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(r.sgst)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(r.igst)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums font-semibold">{fmtINR(r.total_gst)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(r.total)}</td>
                        <td className="px-3 py-2 whitespace-nowrap"><Badge variant="outline" className="rounded-full px-2">{r.status}</Badge></td>
                      </tr>))}
                  </tbody>
                  <tfoot><tr className="bg-slate-50 font-semibold" data-testid="gst-register-total">
                    <td className="px-3 py-2.5" colSpan={tab === "purchase" ? 4 : 5}>TOTAL ({(tab === "purchase" ? purchaseRows : salesRows).length})</td>
                    {["taxable", "cgst", "sgst", "igst", "total_gst", "total"].map((k) => (
                      <td key={k} className="px-3 py-2.5 font-mono tabular-nums whitespace-nowrap">{fmtINR(sumOf(tab === "purchase" ? purchaseRows : salesRows, k))}</td>))}
                    <td />
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}

          {tab === "monthly" && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm" data-testid="gst-monthly">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b border-slate-200">
                  {["Month", "Purchase Taxable", "Input CGST", "Input SGST", "Input IGST", "Total Input GST",
                    "Sales Taxable", "Output CGST", "Output SGST", "Output IGST", "Total Output GST", "Net GST Position"].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
                </tr></thead>
                <tbody>
                  {!data.monthly.length ? <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-400">No GST activity in this period</td></tr>
                    : data.monthly.map((m) => (
                      <tr key={m.month} data-testid={`gst-month-${m.month}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{m.month}</td>
                        {["purchase_taxable", "input_cgst", "input_sgst", "input_igst", "input_total",
                          "sales_taxable", "output_cgst", "output_sgst", "output_igst", "output_total"].map((k) => (
                            <td key={k} className="px-3 py-2 font-mono tabular-nums whitespace-nowrap">{fmtINR(m[k])}</td>))}
                        <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums font-semibold">
                          {fmtINR(Math.abs(m.net))} <span className={`text-[10px] font-sans ${m.net > 0 ? "text-rose-700" : m.net < 0 ? "text-emerald-700" : "text-slate-400"}`}>{m.position}</span>
                        </td>
                      </tr>))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "payments" && (
            <div data-testid="gst-payments">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-500">GST actually paid to the government — recorded separately from the GST calculated on transactions.</p>
                {can("create") && <Button data-testid="gst-add-payment" onClick={() => openPay(null)} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Plus className="w-4 h-4" /> Record GST Payment</Button>}
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    {["Payment Date", "FY", "Tax Period", "Amount Paid", "CGST", "SGST", "IGST", "Challan / Ref", "Mode", "Remarks", ""].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {!data.payments.length ? <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-400" data-testid="gst-no-payments">No GST payment recorded in this period</td></tr>
                      : data.payments.map((p) => (
                        <tr key={p.id} data-testid={`gst-payment-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtDate(p.payment_date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.financial_year || "-"}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.tax_period || "-"}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums font-semibold">{fmtINR(p.amount_paid)}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(p.cgst_paid)}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(p.sgst_paid)}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{fmtINR(p.igst_paid)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.challan_no || "-"}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.payment_mode || "-"}</td>
                          <td className="px-3 py-2 text-slate-500 max-w-[220px] truncate">{p.remarks || "-"}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {can("edit") && <Button data-testid={`gst-payment-edit-${p.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => openPay(p)}><Pencil className="w-4 h-4" /></Button>}
                            {can("delete") && <Button data-testid={`gst-payment-delete-${p.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delPay(p.id)}><Trash2 className="w-4 h-4" /></Button>}
                          </td>
                        </tr>))}
                  </tbody>
                  <tfoot><tr className="bg-slate-50 font-semibold" data-testid="gst-payments-total">
                    <td className="px-3 py-2.5" colSpan={3}>TOTAL PAID</td>
                    <td className="px-3 py-2.5 font-mono tabular-nums">{fmtINR(data.payments_made.total)}</td>
                    <td className="px-3 py-2.5 font-mono tabular-nums">{fmtINR(data.payments_made.cgst)}</td>
                    <td className="px-3 py-2.5 font-mono tabular-nums">{fmtINR(data.payments_made.sgst)}</td>
                    <td className="px-3 py-2.5 font-mono tabular-nums">{fmtINR(data.payments_made.igst)}</td>
                    <td colSpan={4} />
                  </tr></tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="gst-payment-dialog">
          <DialogHeader><DialogTitle className="font-head">{payForm.id ? "Edit GST Payment" : "Record GST Payment"}</DialogTitle>
            <DialogDescription>This records money actually paid to the government. It never changes the GST calculated on your bills.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><span className="text-xs text-slate-500">Payment Date *</span><Input data-testid="gst-pay-date" type="date" value={payForm.payment_date || ""} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Financial Year</span><Input data-testid="gst-pay-fy" placeholder="2026-27" value={payForm.financial_year || ""} onChange={(e) => setPayForm({ ...payForm, financial_year: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Tax Period</span><Input data-testid="gst-pay-period" placeholder="Jun 2026 / Q1" value={payForm.tax_period || ""} onChange={(e) => setPayForm({ ...payForm, tax_period: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Amount Paid</span><Input data-testid="gst-pay-amount" type="number" value={payForm.amount_paid ?? ""} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">CGST Paid</span><Input data-testid="gst-pay-cgst" type="number" value={payForm.cgst_paid ?? ""} onChange={(e) => setPayForm({ ...payForm, cgst_paid: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">SGST Paid</span><Input data-testid="gst-pay-sgst" type="number" value={payForm.sgst_paid ?? ""} onChange={(e) => setPayForm({ ...payForm, sgst_paid: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">IGST Paid</span><Input data-testid="gst-pay-igst" type="number" value={payForm.igst_paid ?? ""} onChange={(e) => setPayForm({ ...payForm, igst_paid: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Challan / Reference No</span><Input data-testid="gst-pay-challan" value={payForm.challan_no || ""} onChange={(e) => setPayForm({ ...payForm, challan_no: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Payment Mode</span>
              <Select value={payForm.payment_mode || "Bank Transfer"} onValueChange={(v) => setPayForm({ ...payForm, payment_mode: v })}>
                <SelectTrigger data-testid="gst-pay-mode" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><span className="text-xs text-slate-500">Remarks</span><Textarea data-testid="gst-pay-remarks" value={payForm.remarks || ""} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} /></div>
          <p className="text-[11px] text-slate-500">Leave Amount Paid blank to use CGST + SGST + IGST. If you fill the split, it must add up to the amount paid.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button data-testid="gst-pay-save" disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={savePay}>{saving ? "Saving..." : "Save Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
